import { useMemo, useState } from "react";
import { CHART_LAYOUT, buildChartModel } from "./vampChart";
import type { VampMonthContext } from "./vampStanding";
import type { VampDay } from "../../../types/vampRates";

interface VampTrendChartProps {
  days: VampDay[];
  ctx: VampMonthContext;
  showFloor: boolean;
}

const AXIS_LABEL = "absolute text-[10px] leading-[14px] text-gray-400";

const { viewBox, height, plotHeight, topY, baseY, plotSpan, gutterL, axisX2, zeroTop, xLabelTop, labelPrevPct, labelRightPct } =
  CHART_LAYOUT;

function ReadoutRow({ label, value, swatch }: { label: string; value: string; swatch?: string }) {
  return (
    <span className="flex items-center gap-2 border-b border-gray-50 px-3 py-[5px]">
      <span className={`h-[9px] w-[9px] shrink-0 rounded-sm ${swatch ?? ""}`} />
      <span className="text-theme-xs text-gray-600">{label}</span>
      <span className="ml-auto text-theme-xs font-semibold text-gray-900 tabular-nums">{value}</span>
    </span>
  );
}

// Bars are the events accruing within each month; the line is the month-to-date rate. Left axis
// counts, right axis the ratio: the two Visa tests on one timeline.
export default function VampTrendChart({ days, ctx, showFloor }: VampTrendChartProps) {
  const m = useMemo(() => buildChartModel(days, ctx, showFloor), [days, ctx, showFloor]);
  const [hover, setHover] = useState<number | null>(null);

  return (
    <div className="relative" onMouseLeave={() => setHover(null)}>
      <div className="relative">
        <svg viewBox={viewBox} preserveAspectRatio="none" style={{ display: "block", width: "100%", height: height }} aria-hidden="true">
          {m.showToday && <rect x={m.todayX} y={topY} width={m.futureW} height={plotSpan} fill="#fcfcfd" />}
          <line x1={gutterL} y1={baseY} x2={axisX2} y2={baseY} stroke="#e4e7ec" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          {m.bars.map((b) => (
            <g key={b.date}>
              <rect x={b.x} y={b.yAll} width={m.barWidth} height={b.hAll} rx={2} fill="#2a31d8" />
              <rect x={b.x} y={b.y40} width={m.barWidth} height={b.h40} rx={2} fill="#7592ff" />
            </g>
          ))}
          {m.showFloor && (
            <line x1={gutterL} y1={m.floorY} x2={axisX2} y2={m.floorY} stroke="#d0d5dd" strokeWidth={1} strokeDasharray="2 4" vectorEffect="non-scaling-stroke" />
          )}
          <line x1={m.boundaryX} y1={topY} x2={m.boundaryX} y2={baseY} stroke="#d0d5dd" strokeWidth={1} vectorEffect="non-scaling-stroke" />
          <line x1={gutterL} y1={m.rateY} x2={axisX2} y2={m.rateY} stroke="#d92d20" strokeWidth={2} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
          {/* White casing under the rate line keeps it legible where it crosses bars or a guide. */}
          <path d={m.ratePath} fill="none" stroke="#ffffff" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          <path d={m.ratePath} fill="none" stroke="#1d2939" strokeWidth={2.25} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </svg>

        <span className={`${AXIS_LABEL} left-0 w-[38px] text-right`} style={{ top: zeroTop }}>0</span>
        {m.showFloor && (
          <span className={`${AXIS_LABEL} left-0 w-[38px] text-right tabular-nums`} style={{ top: m.floorTop }}>{m.floorLabel}</span>
        )}
        <span className={`${AXIS_LABEL} right-0 w-[34px] text-error-700 tabular-nums`} style={{ top: m.rateTop }}>1.5%</span>

        {m.restBar && hover === null && (
          <>
            <span
              className="absolute -ml-1 h-2 w-2 rounded-full bg-gray-800 shadow-[0_0_0_2px_#ffffff]"
              style={{ left: `${m.restBar.centerPct}%`, top: m.restBar.dotTop }}
            />
            <span
              className="absolute -translate-x-1/2 text-[11px] font-semibold leading-[15px] text-gray-800 tabular-nums"
              style={{ left: `${m.restBar.centerPct}%`, top: m.restBar.valTop }}
            >
              {m.restBar.mtd}
            </span>
          </>
        )}

        <span className={`${AXIS_LABEL} left-0`} style={{ top: xLabelTop }}>Count</span>
        <span className={AXIS_LABEL} style={{ left: `${labelPrevPct}%`, top: xLabelTop }}>{m.prevMonthLabel}</span>
        <span className={`${AXIS_LABEL} -translate-x-1/2`} style={{ left: `${m.labelMonthPct}%`, top: xLabelTop }}>{m.monthLabel}</span>
        {m.todayLabel && (
          <span className={`${AXIS_LABEL} -translate-x-1/2 font-semibold text-gray-600`} style={{ left: `${m.todayLabelPct}%`, top: xLabelTop }}>
            {m.todayLabel}
          </span>
        )}
        <span className={AXIS_LABEL} style={{ right: `${labelRightPct}%`, top: xLabelTop }}>{m.lastDayLabel}</span>
        <span className={`${AXIS_LABEL} right-0`} style={{ top: xLabelTop }}>Ratio</span>
      </div>

      <div className="absolute inset-x-0 top-0" style={{ height: plotHeight }}>
        {m.bars.map((b, i) => (
          <div
            key={b.date}
            data-testid="vamp-chart-day"
            className="absolute top-0 h-full"
            style={{ left: `${b.hitLeftPct}%`, width: `${b.hitWidthPct}%` }}
            onMouseEnter={() => setHover(i)}
          >
            {hover === i && (
              <>
                <span className="absolute inset-0 rounded bg-brand-500/[0.07]" />
                <span className="absolute bottom-0 left-1/2 top-0 w-px bg-gray-300" />
                <span className="absolute left-1/2 -ml-1 h-2 w-2 rounded-full bg-gray-800 shadow-[0_0_0_2px_#ffffff]" style={{ top: b.dotTop }} />
                <span className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold leading-[15px] text-gray-800 tabular-nums" style={{ top: b.valTop }}>
                  {b.mtd}
                </span>
                <div
                  className={`absolute bottom-0 z-20 w-[262px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_10px_26px_rgba(16,24,40,0.13)] ${
                    b.cardOnLeft ? "right-[calc(100%+10px)]" : "left-[calc(100%+10px)]"
                  }`}
                >
                  <div className="border-b border-gray-100 px-3 py-2 text-theme-xs font-semibold text-gray-900">{b.day}</div>
                  <ReadoutRow label="VAMP rate, month to date" value={b.mtd} />
                  <ReadoutRow label="Events, month to date" value={b.eventsMtd} />
                  <ReadoutRow label="Events that day" value={b.events} />
                  <ReadoutRow label="Fraud reports (TC40)" value={b.tc40} swatch="bg-brand-400" />
                  <ReadoutRow label="Disputes (TC15)" value={b.tc15} swatch="bg-brand-700" />
                  <ReadoutRow label="Transactions" value={b.settled} />
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
